import React, { useState, useEffect, useCallback } from "react";
import { TableCell, Autocomplete, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import type { CrewMember, Role, Stream } from "../types/crew-assignment";
import CustomTextField from "../../../components/common/Textfield";
import apiService from "../../../service/Api";

// Debounce hook
function useDebounce(value: string, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

const StyledAssignmentCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1),
  verticalAlign: "top",
  "& .MuiAutocomplete-root": {
    minWidth: 220,
  },
}));

interface AssignmentCellProps {
  role: Role;
  event: Stream;
  selectedCrew?: CrewMember; // single value
  onCrewChange: (crewMember: CrewMember | null) => void;
}

const AssignmentCell: React.FC<AssignmentCellProps> = ({
  role: _role,
  event: _event,
  selectedCrew,
  onCrewChange,
}) => {
  void _role;
  void _event;
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(false);

  const debouncedSearch = useDebounce(inputValue);

  const fetchCrewMembers = useCallback(async (search: string) => {
    setLoading(true);
    try {
      const response = await apiService.get<{ results: CrewMember[] }>(
        "/api/crews/crew-members/",
        { search, page_size: 10 }
      );
      setOptions(response.data.results || []);
    } catch (err) {
      console.error("Failed to fetch crew members:", err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debouncedSearch) {
      fetchCrewMembers(debouncedSearch);
    } else {
      setOptions([]);
    }
  }, [debouncedSearch, fetchCrewMembers]);

  const getCrewMemberName = (crew: CrewMember) => {
    const firstName = crew.user?.first_name || "";
    const lastName = crew.user?.last_name || "";
    return firstName || lastName ? `${firstName} ${lastName}`.trim() : "";
  };

  return (
    <StyledAssignmentCell>
      <Autocomplete
        options={options}
        getOptionLabel={getCrewMemberName}
        value={selectedCrew || null}
        onChange={(_, newValue) => onCrewChange(newValue)}
        inputValue={inputValue}
        onInputChange={(_, newInput) => setInputValue(newInput)}
        loading={loading}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <CustomTextField
            {...params}
            placeholder="Search crew..."
            variant="outlined"
            helperText="Type 3 letters to search"
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </StyledAssignmentCell>
  );
};

export default AssignmentCell;
